import { Link } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, FileText, Loader2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";

export interface IngestJob {
  id: string;
  status: "queued" | "converting" | "classified" | "indexed" | "failed" | "quarantined";
  source_type: "github" | "email" | "upload" | "paste" | "url";
  original_filename: string;
  size_bytes: number;
  detected_project?: string;
  detected_lang?: string;
  confidence?: number;
  target_doc_id?: string;
  error?: string;
  created_at: string;
}

const STATUS_LABEL: Record<IngestJob["status"], { label: string; tone: string }> = {
  queued: { label: "En cola", tone: "muted" },
  converting: { label: "Convirtiendo", tone: "info" },
  classified: { label: "Clasificado", tone: "info" },
  indexed: { label: "Indexado", tone: "success" },
  failed: { label: "Error", tone: "error" },
  quarantined: { label: "En revisión", tone: "warning" },
};

export function JobCard({ job }: { job: IngestJob }) {
  const meta = STATUS_LABEL[job.status];
  const inProgress = job.status === "queued" || job.status === "converting";
  const icon =
    job.status === "failed"
      ? AlertTriangle
      : job.status === "indexed"
        ? CheckCircle2
        : inProgress
          ? Loader2
          : FileText;
  const Icon = icon;

  return (
    <div className="rounded-md border border-(--border-subtle) bg-(--bg-elevated) p-3">
      <header className="flex items-center gap-2">
        <Icon
          size={16}
          className={`${inProgress ? "animate-spin text-(--accent-500)" : "text-(--text-tertiary)"}`}
        />
        <div className="flex-1 truncate text-sm font-medium">{job.original_filename}</div>
        <Badge variant={meta.tone === "success" ? "default" : "muted"} className="text-[10px]">
          {meta.label}
        </Badge>
      </header>
      <div className="mt-1 text-xs text-(--text-tertiary)">
        {job.source_type} · {(job.size_bytes / 1024).toFixed(1)} kB ·{" "}
        {new Date(job.created_at).toLocaleTimeString()}
      </div>
      {job.detected_project && (
        <div className="mt-1 text-xs">
          Proyecto detectado: <strong>{job.detected_project}</strong>
          {job.confidence != null && ` · ${(job.confidence * 100).toFixed(0)}% confianza`}
        </div>
      )}
      {job.error && <div className="mt-1 text-xs text-(--error)">{job.error}</div>}
      {job.status === "indexed" && job.target_doc_id && (
        <Button variant="ghost" size="sm" className="mt-1" asChild>
          <Link to="/docs/$docId" params={{ docId: job.target_doc_id }}>
            Abrir documento →
          </Link>
        </Button>
      )}
      {job.status === "quarantined" && (
        <div className="mt-1 flex gap-1">
          <Button variant="ghost" size="sm">
            Promover
          </Button>
          <Button variant="ghost" size="sm">
            Descartar
          </Button>
        </div>
      )}
    </div>
  );
}
